import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { Pagination } from 'components/molecules/Pagination';
import {
  ComponentsTableColumns,
  mapComponentsToDataSource,
} from 'pages/AncillariesPage/helpers';
import { ERouteLinks } from 'models/route';
import { Table } from 'components/molecules/Table';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useAlert } from 'models/alertContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ActionTypes } from 'state/actions/alert';
import { CardList } from 'components/molecules/CardList';
import { Loader } from 'components/atoms/Loader';
import { ComponentsSearchForm } from 'pages/AncillariesPage/ComponentsPage/ComponentsSearchForm';
import { ComponentsCard } from 'pages/AncillariesPage/ComponentsPage/ComponentsCard';
import { EPerPages, ISortOptions, ITableDataSource } from 'models/table';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import {
  customFieldsPrepareToSearch,
  objectToQueryString,
  prepareDataWithCustomFieldsToSearch,
  prepareQueryValues,
} from 'helpers/searchQuery';
import {
  IFormElementOptionProp,
  initialAncillariesSearchFormValues,
  ISearchAncillariesFormValues,
} from 'models/form';
import SearchFilterStore from 'state/SearchFilterStore';
import { useQuery } from 'hooks/useQuery';
import {
  EAncillaryFilterTypes,
  EArtAncillaryExpandType,
  EArtAncillaryType,
  EArtAncillaryTypeName,
} from 'constants/ArtAccessoryType';
import { IComponent } from 'models/ancillaries';
import { EPermission } from 'models/permissions';
import {
  ECustomFieldObjectType,
  ECustomFieldType,
  ICustomFieldByObjectType,
} from 'models/customField';
import { useLang } from 'models/langContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EChipsVariants } from 'components/atoms/Chips';
import { renderRowColor, renderRowColorHover } from 'components/molecules/Table/helpers';
import { Tooltip } from '@mui/material';
import { isExportNotAvailable } from 'constants/Export';
import { EFirearmStateId } from 'models/firearm';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { Box } from '@mui/system';
import { ISearchFormCustomFieldValue } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { replaceUrlIfOnPage } from 'utils/history';
import { exportAncillariesCSV } from '../../../requests/report';
import { useAncillaries } from '../hooks';
import { EAssetType } from 'models/assetType';
import { joinValuesToString } from 'helpers/joinValuesToString';
import { useDictionary } from 'models/dictionaryContext';

interface ComponentsPageProps {
  renderDataColor: (data: ITableDataSource) => {
    border: string;
    stateField: EChipsVariants;
    background: string;
  };
  setComponentsAccordionExpanded: (val: boolean | undefined) => void;
}

const ComponentsPage = ({
  renderDataColor,
  setComponentsAccordionExpanded,
}: ComponentsPageProps) => {
  const { isDesktop } = useThemeBreakpoint();
  const { t } = useTranslations();
  const { pathname }: any = useLocation();
  const navigate = useNavigate();
  const [isComponentsLoading, setIsComponentsLoading] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const urlQuery = new URLSearchParams(searchParams);
  const expandType = urlQuery.get('expand');
  const perPageFromQuery = Number.parseInt(searchParams.get('perPage') || '25', 10);
  const currentPageFromQuery = Number.parseInt(searchParams.get('currentPage') || '1', 10);
  const [currentPageComponents, setCurrentPageComponents] = useState<number>(
    expandType === EArtAncillaryExpandType.COMPONENTS ? currentPageFromQuery : 1,
  );
  const [perPageComponents, setPerPageComponents] = useState<EPerPages>(
    expandType === EArtAncillaryExpandType.COMPONENTS ? perPageFromQuery : EPerPages.perPage25,
  );
  const [componentsSort, setComponentsSort] = useState<ISortOptions | null>(null);
  const [componentsQuery, setComponentsQuery] = useState<
    Partial<ISearchAncillariesFormValues>
  >({});
  const [isComponentsQueryLoading, setIsComponentsQueryLoading] = useState<boolean>(false);
  const [isSortLoaded, setIsSortLoaded] = useState<boolean>(true);
  const query = useQuery();
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [ownerOptions, setOwnerOptions] = useState<IFormElementOptionProp[]>([]);
  const [keeperOptions, setKeeperOptions] = useState<IFormElementOptionProp[]>([]);
  const [initialValues, setInitialValues] = useState<ISearchAncillariesFormValues | null>(
    null,
  );
  const [areInitialValuesLoaded, setAreInitialValuesLoaded] = useState(false);
  const [areCustomFieldsLoaded, setAreCustomFieldsLoaded] = useState(false);
  const { selectedLanguage } = useLang();
  const { shortDateFormat } = useGlobalProperty();
  const { state: routerState }: any = useLocation();
  const isFromMapQuery: boolean =
    query.get('fromMap') === 'true' || routerState?.componentsUniqueIds?.length > 0;
  const storedComponents = SearchFilterStore.getComponents();
  const storedMapBounds = SearchFilterStore.getMapBounds();
  const [isExportLoading, setExportLoading] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { setAlert, clearAlert, componentAlert } = useAlert();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const [markedText, setMarkedText] = useState<string | undefined>();
  const {
    getMake,
    make,
    getModel,
    model,
    getFirearmType,
    firearmType,
    artComponentManufacturer,
    getArtComponentManufacturer,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getFirearmType();
      getArtComponentManufacturer();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const scrollPosition = useRef(0);

  const uniqueIds =
    storedComponents.length > 0
      ? storedComponents.map((componentItem: IComponent) => componentItem.uniqueId)
      : routerState?.componentsUniqueIds;

  const {
    paginatorComponents,
    components,
    getComponentsData,
    getCustomField,
    customFieldsComponents,
  } = useAncillaries();

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_COMPONENT_ALERT);
      setIsComponentsLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    (async () => {
      setAreCustomFieldsLoaded(false);
      await getCustomField(ECustomFieldObjectType.AN_COMPONENT);
      setAreCustomFieldsLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousComponentsSortRef = useRef(componentsSort);

  useEffect(() => {
    (async function getDataInit() {
      if (!components.length) {
        setIsComponentsLoading(true);
      }
      setIsPaginationLoading(true);

      if (isFromMapQuery) {
        if (storedMapBounds && storedMapBounds.assetType === EAssetType.COMPONENT) {
          componentsQuery.minLatitude = storedMapBounds.minLatitude;
          componentsQuery.maxLatitude = storedMapBounds.maxLatitude;
          componentsQuery.minLongitude = storedMapBounds.minLongitude;
          componentsQuery.maxLongitude = storedMapBounds.maxLongitude;

          delete componentsQuery.uniqueIds;
        } else if (uniqueIds && uniqueIds.length) {
          componentsQuery.uniqueIds = uniqueIds;

          delete componentsQuery.minLatitude;
          delete componentsQuery.maxLatitude;
          delete componentsQuery.minLongitude;
          delete componentsQuery.maxLongitude;
        } else {
          delete componentsQuery.uniqueIds;

          delete componentsQuery.minLatitude;
          delete componentsQuery.maxLatitude;
          delete componentsQuery.minLongitude;
          delete componentsQuery.maxLongitude;
        }
      }

      if (componentsSort !== previousComponentsSortRef.current) {
        setIsSortLoaded(false);
      }
      if (Object.keys(componentsQuery).length !== 0) {
        customFieldsPrepareToSearch(componentsQuery.customFields);
        const componentsQueryData = prepareDataWithCustomFieldsToSearch(
          componentsQuery,
          customFieldsComponents.filter(
            (item: ICustomFieldByObjectType) => item.searchCriteria,
          ),
        );
        await getComponentsData(
          currentPageComponents,
          perPageComponents,
          {
            ...componentsQueryData,
          },
          componentsSort,
        );
        setIsComponentsLoading(false);
      }

      setIsComponentsQueryLoading(false);
      setIsPaginationLoading(false);
      setIsSortLoaded(true);
      previousComponentsSortRef.current = componentsSort;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentsQuery, componentsSort, selectedLanguage]);

  useEffect(() => {
    if (initialValues !== undefined && areCustomFieldsLoaded) {
      setAreInitialValuesLoaded(true);
    }
  }, [initialValues, areCustomFieldsLoaded]);

  useEffect(() => {
    if (areCustomFieldsLoaded) {
      setAreInitialValuesLoaded(false);
      if (expandType === EArtAncillaryExpandType.COMPONENTS) {
        setShowAdvanced(true);
        const queryValues = prepareQueryValues(
          {
            ...initialAncillariesSearchFormValues,
            perPage: perPageComponents,
            currentPage: currentPageComponents,
          },
          urlQuery,
          [
            ...customFieldsComponents
              .filter((cf) => cf.customFieldType === ECustomFieldType.DATE)
              .map((cf) => [`from_${cf.name}`, `to_${cf.name}`]),
          ].flat(),
        );
        setInitialValues({ ...initialAncillariesSearchFormValues, ...queryValues });
      } else {
        setInitialValues(initialAncillariesSearchFormValues);
        setAreInitialValuesLoaded(true);
      }
    }
  }, [searchParams, areCustomFieldsLoaded]);

  const getNewQueryPath = (values: Partial<ISearchAncillariesFormValues>): string => {
    const valuesWithoutPreset = { ...values };
    valuesWithoutPreset.preset = undefined;
    valuesWithoutPreset.formSubmitted = undefined;
    valuesWithoutPreset.perPage = perPageComponents;
    valuesWithoutPreset.currentPage = currentPageComponents;

    const valuesToString = {
      ...valuesWithoutPreset,
      accessoryCapacities: joinValuesToString(values.accessoryCapacities),
      calibresUniqueIds: joinValuesToString(values.calibresUniqueIds),
      cartridgeCompositionsUniqueIds: joinValuesToString(
        values.cartridgeCompositionsUniqueIds,
      ),
      coloursOfPrimerSealantUniqueIds: joinValuesToString(
        values.coloursOfPrimerSealantUniqueIds,
      ),
      componentCapacitiesUniqueIds: joinValuesToString(values.componentCapacitiesUniqueIds),
      componentmanufacturersUniqueIds: joinValuesToString(
        values.componentmanufacturersUniqueIds,
      ),
      compositionsUniqueIds: joinValuesToString(values.compositionsUniqueIds),
      factoriesUniqueIds: joinValuesToString(values.factoriesUniqueIds),
      frtMakesUniqueIds: joinValuesToString(values.frtMakesUniqueIds),
      frtManufacturersUniqueIds: joinValuesToString(values.frtManufacturersUniqueIds),
      frtModelsUniqueIds: joinValuesToString(values.frtModelsUniqueIds),
      frtTypesUniqueIds: joinValuesToString(values.frtTypesUniqueIds),
      functionalTypesUniqueIds: joinValuesToString(values.functionalTypesUniqueIds),
      hostTypesUniqueIds: joinValuesToString(values.hostTypesUniqueIds),
      lightColoursUniqueIds: joinValuesToString(values.lightColoursUniqueIds),
      magnificationsUniqueIds: joinValuesToString(values.magnificationsUniqueIds),
      manufacturersUniqueIds: joinValuesToString(values.manufacturersUniqueIds),
      munitionTypesUniqueIds: joinValuesToString(values.munitionTypesUniqueIds),
      munitionVelocitiesUniqueIds: joinValuesToString(values.munitionVelocitiesUniqueIds),
      projectileShapesUniqueIds: joinValuesToString(values.projectileShapesUniqueIds),
      proofHousesUniqueIds: joinValuesToString(values.proofHousesUniqueIds),
      reticlesUniqueIds: joinValuesToString(values.reticlesUniqueIds),
      shotSizesUniqueIds: joinValuesToString(values.shotSizesUniqueIds),
      statesUniqueIds: joinValuesToString(values.statesUniqueIds),
      utilitiesUniqueIds: joinValuesToString(values.utilitiesUniqueIds),
    };

    setMarkedText(values.marking);

    const objQuery = objectToQueryString(valuesToString);

    if (
      objQuery.length &&
      (!isInitialLoad || expandType === EArtAncillaryExpandType.COMPONENTS)
    ) {
      setIsInitialLoad(false);
      return `${pathname}?expand=components&${objQuery}`;
    }
    if (values.formSubmitted === true) {
      return `${pathname}?expand=components`;
    }
    setIsInitialLoad(false);
    return `${pathname}`;
  };

  const areCustomFieldValuesSame = (
    cfValues: ISearchFormCustomFieldValue[] | undefined,
    queryValues: ISearchFormCustomFieldValue[] | undefined,
  ): boolean => {
    if (!cfValues || !queryValues) {
      return true;
    }
    let areSame = true;
    cfValues.forEach((cfValue) => {
      const queryCf = queryValues.find(
        (it) => it.customFieldUniqueId === cfValue.customFieldUniqueId,
      );
      if (
        cfValue.bool !== queryCf?.bool ||
        cfValue.text !== queryCf?.text ||
        cfValue.dateFrom !== queryCf?.dateFrom ||
        cfValue.dateTo !== queryCf?.dateTo
      ) {
        areSame = false;
      }
    });

    return areSame;
  };

  const handleSetComponentsQuery = (values: Partial<ISearchAncillariesFormValues>) => {
    if (values.invokedByPageChange !== true) {
      setIsComponentsQueryLoading(true);
    }
    values.invokedByPageChange = undefined;

    const valuesWithoutPreset = { ...values };
    valuesWithoutPreset.preset = undefined;
    valuesWithoutPreset.formSubmitted = undefined;

    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      replaceUrlIfOnPage(getNewQueryPath(values));

      if (
        values.ancillaryTypeUniqueId !== componentsQuery.ancillaryTypeUniqueId ||
        values.fireCycleId !== componentsQuery.fireCycleId ||
        values.serialNumber !== componentsQuery.serialNumber ||
        values.exhibitNumber !== componentsQuery.exhibitNumber ||
        values.manufacturerUniqueId !== componentsQuery.manufacturerUniqueId ||
        values.productName !== componentsQuery.productName ||
        values.calibreUniqueId !== componentsQuery.calibreUniqueId ||
        values.length !== componentsQuery.length ||
        values.compositionUniqueId !== componentsQuery.compositionUniqueId ||
        values.componentCapacityUniqueId !== componentsQuery.componentCapacityUniqueId ||
        values.proofHouseUniqueId !== componentsQuery.proofHouseUniqueId ||
        values.stateUniqueId !== componentsQuery.stateUniqueId ||
        values.legalityUniqueId !== componentsQuery.legalityUniqueId ||
        !areCustomFieldValuesSame(values.customFields, componentsQuery.customFields) ||
        values.keeperUniqueId !== componentsQuery.keeperUniqueId ||
        values.ownerUniqueId !== componentsQuery.ownerUniqueId ||
        values.frtTypeUniqueId !== componentsQuery.frtTypeUniqueId ||
        values.frtMakeUniqueId !== componentsQuery.frtMakeUniqueId ||
        values.frtModelUniqueId !== componentsQuery.frtModelUniqueId ||
        values.frtManufacturerUniqueId !== componentsQuery.frtManufacturerUniqueId
      ) {
        setCurrentPageComponents(1);
      }
    }

    setComponentsQuery(valuesWithoutPreset);
    if (
      routerState?.presetAncillaries &&
      routerState?.ancillaryFilterType === EAncillaryFilterTypes.COMPONENT
    ) {
      setComponentsAccordionExpanded(true);
    }
  };

  const exportCsv = async () => {
    setExportLoading(true);
    componentsQuery.ancillaryDictionaryUniqueId = EArtAncillaryType.COMPONENTS;
    await exportAncillariesCSV(componentsQuery, componentsSort);
    setExportLoading(false);
  };

  const handleRedirectToEditPageButton = (item: ITableDataSource) => {
    if (customFieldsComponents?.length && item.stateUniqueId !== EFirearmStateId.DISABLED) {
      navigate(
        ERouteLinks.EditAncillary.replace(':id', item.id as string).replace(
          ':type',
          'component',
        ),
      );
    } else if (item.stateUniqueId === EFirearmStateId.DISABLED) {
      setAlert(ActionTypes.SET_COMPONENT_ALERT, {
        text: t('ancillary.disabled.edit.info'),
        variant: EAlertVariants.error,
      } as IAlert);
    } else {
      setAlert(ActionTypes.SET_COMPONENT_ALERT, {
        text: t('general.noAvaibleDataToEdit.label'),
        variant: EAlertVariants.warning,
      } as IAlert);
    }
  };

  return (
    <div>
      {initialValues && areInitialValuesLoaded && areCustomFieldsLoaded && (
        <ComponentsSearchForm
          ownerOptions={ownerOptions}
          keeperOptions={keeperOptions}
          setOwnerOptions={setOwnerOptions}
          setKeeperOptions={setKeeperOptions}
          showAdvanced={showAdvanced}
          componentsUniqueIds={uniqueIds}
          isFromMap={isFromMapQuery}
          isSearchButtonDisabled={isComponentsLoading || isComponentsQueryLoading}
          returnPath={`${pathname}?expand=components`}
          getNewQueryPath={getNewQueryPath}
          setShowAdvanced={setShowAdvanced}
          onSubmit={handleSetComponentsQuery}
          initialFormValues={initialValues ?? undefined}
          customFields={customFieldsComponents.filter(
            (item: ICustomFieldByObjectType) => item.searchCriteria,
          )}
          currentPage={currentPageComponents}
          perPage={perPageComponents}
        />
      )}
      <Grid sx={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0', gap: 1 }}>
        {!isComponentsLoading && (
          <>
            {isExportNotAvailable(paginatorComponents) ? (
              <Tooltip title={t('dataExportRecordsLimitAmount.label')} arrow>
                <span>
                  <Button
                    variant={EButtonVariants.outlined}
                    size={EButtonSizes.medium}
                    permission={[EPermission.EXPORT_ANCILLARIES]}
                    label={t('ancillaries.accordion.export.button')}
                    type="submit"
                    onClick={exportCsv}
                    isLoading={isExportLoading}
                    disabled
                  />
                </span>
              </Tooltip>
            ) : (
              <Button
                variant={EButtonVariants.outlined}
                size={EButtonSizes.medium}
                permission={[EPermission.EXPORT_ANCILLARIES]}
                label={t('ancillaries.accordion.export.button')}
                type="submit"
                onClick={exportCsv}
                isLoading={isExportLoading}
              />
            )}
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.medium}
              label={t('ancillaries.accordion.showOnMap.button')}
              onClick={() => {
                navigate(ERouteLinks.RegisteredAncillariesMap, {
                  state: {
                    ancillaryType: 'component',
                    ancillaryQuery: componentsQuery,
                    showAdvanced,
                    options: { ownerOptions, keeperOptions },
                    navigateBack: -1,
                  },
                });
              }}
            />
          </>
        )}
      </Grid>
      {isComponentsLoading || isComponentsQueryLoading ? (
        <Loader isLoading={true} />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={ComponentsTableColumns(
                {
                  handleEdit: handleRedirectToEditPageButton,
                  customFields: customFieldsComponents,
                  t,
                  markedText,
                  isAnyLicenceAuthorityValid,
                  showOnMap: (item) => {
                    navigate(ERouteLinks.RegisteredAncillariesMap, {
                      state: {
                        ancillaryUniqueId: item?.id,
                        ancillaryType: EArtAncillaryTypeName.COMPONENTS.toLowerCase(),
                        legalityUniqueId: item?.legalityUniqueId,
                        navigateBack: `${ERouteLinks.Ancillaries}?expand=component`,
                        showAdvanced: true,
                        showLegalityBar: false,
                        breadcrumbs: [
                          {
                            label: t('module.ancillaries.name'),
                            route: ERouteLinks.Ancillaries,
                          },
                          {
                            label: t('module.component.name'),
                            route: `${ERouteLinks.Ancillaries}?expand=component`,
                          },
                          t('ancillaries.ancillariesLocation.showOnMap.label'),
                        ],
                      },
                    });
                  },
                },
                (data: ITableDataSource) => renderDataColor(data).stateField,
                selectedLanguage?.uniqueId,
                shortDateFormat,
              )}
              specificRowColor={renderRowColor}
              specificRowColorHover={renderRowColorHover}
              dataSource={mapComponentsToDataSource(
                components,
                make,
                model,
                firearmType,
                artComponentManufacturer,
              )}
              onSort={(it) => {
                setComponentsSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              customPaddingIfNoData="30px"
            />
          ) : (
            <CardList
              items={mapComponentsToDataSource(
                components,
                make,
                model,
                firearmType,
                artComponentManufacturer,
              )}
              render={(data, index) => (
                <ComponentsCard
                  data={data}
                  key={index}
                  renderColor={renderDataColor}
                  handleView={(id: string) =>
                    navigate(
                      ERouteLinks.ViewAncillary.replace(':id', id).replace(
                        ':type',
                        'component',
                      ),
                    )
                  }
                  handleEdit={handleRedirectToEditPageButton}
                  hasCustomFields={!!customFieldsComponents.length}
                  isAnyLicenceAuthorityValid={isAnyLicenceAuthorityValid}
                />
              )}
            />
          )}

          {paginatorComponents && (
            <Pagination
              count={paginatorComponents?.totalElements}
              perPage={perPageComponents}
              onChangePage={(page) => {
                setCurrentPageComponents(page);
              }}
              onChangePerPage={(value) => {
                setPerPageComponents(value);
              }}
              current={currentPageComponents}
              isLoading={isPaginationLoading}
              isVisible={isSortLoaded}
            />
          )}
        </>
      )}
      {componentAlert && (
        <Box sx={{ marginBottom: '10px', mt: 2 }}>
          <Alert text={componentAlert.text} variant={componentAlert.variant} />
        </Box>
      )}
    </div>
  );
};

export default ComponentsPage;
